import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CircleNotch, User, House, Users, Bank, CheckCircle, ArrowRight, ArrowLeft, Sparkle } from "@phosphor-icons/react";

/**
 * Drake Demographics Wizard — collects Filer / Dependents / Bank fields and
 * emits them as canonical MeF paths for every open engagement (see
 * portal_pass_b.MEF_FILER_PATHS + /api/portal/onboarding endpoints).
 */
const STEPS = [
  { id: "you", label: "About you", icon: User },
  { id: "address", label: "Address", icon: House },
  { id: "family", label: "Filing & family", icon: Users },
  { id: "bank", label: "Bank & sensitive", icon: Bank },
  { id: "review", label: "Review", icon: Sparkle },
];

const FILING_STATUSES = [
  { v: "Single", label: "Single" },
  { v: "MFJ", label: "Married filing jointly" },
  { v: "MFS", label: "Married filing separately" },
  { v: "HoH", label: "Head of household" },
  { v: "QSS", label: "Qualifying surviving spouse" },
];

function Field({ label, children, hint, testid }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1" data-testid={testid && `${testid}-label`}>
        {label}
      </label>
      {children}
      {hint && <p className="text-[10px] text-zinc-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function DemographicsWizard({ trigger }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const { data: session, isLoading } = useQuery({
    queryKey: ["demographics-onboarding"],
    queryFn: () => api.get("/portal/onboarding").then((r) => r.data).catch(() => null),
    enabled: open,
  });

  const [form, setForm] = useState({});

  useEffect(() => {
    if (session?.data) {
      setForm({
        first_name: "",
        middle_initial: "",
        last_name: "",
        ssn: "",
        dob: "",
        occupation: "",
        phone: "",
        email: "",
        address_line1: "",
        address_city: "",
        address_state: "",
        address_zip: "",
        filing_status: "",
        spouse: { first_name: "", last_name: "", ssn: "", dob: "" },
        dependents: [],
        bank_routing: "",
        bank_account: "",
        bank_type: "checking",
        ...(session.data || {}),
      });
      if (typeof session.step === "number" && !session.completed) {
        setStepIdx(Math.min(session.step - 1, STEPS.length - 1));
      }
    }
  }, [session]);

  const patch = useMutation({
    mutationFn: (payload) => api.patch("/portal/onboarding", payload),
    onError: (e) => toast.error(errText(e)),
  });

  const submit = useMutation({
    mutationFn: () => api.post("/portal/onboarding/submit"),
    onSuccess: (r) => {
      toast.success(`Onboarding complete — ${r.data.emitted_fields} fields sent to Drake pipeline across ${r.data.engagements} engagements`);
      qc.invalidateQueries({ queryKey: ["demographics-onboarding"] });
      qc.invalidateQueries({ queryKey: ["drake-fields"] });
      setStepIdx(STEPS.length - 1);
      setTimeout(() => setOpen(false), 900);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setSpouse = (k, v) => setForm((s) => ({ ...s, spouse: { ...(s.spouse || {}), [k]: v } }));
  const addDependent = () =>
    setForm((s) => ({ ...s, dependents: [...(s.dependents || []), { first_name: "", last_name: "", ssn: "", relationship: "child", months_lived: "12" }] }));
  const setDep = (i, k, v) =>
    setForm((s) => {
      const deps = [...(s.dependents || [])];
      deps[i] = { ...deps[i], [k]: v };
      return { ...s, dependents: deps };
    });
  const removeDep = (i) =>
    setForm((s) => {
      const deps = [...(s.dependents || [])];
      deps.splice(i, 1);
      return { ...s, dependents: deps };
    });

  const savePatch = () => patch.mutate({ step: stepIdx + 1, data: form });

  const goNext = () => {
    savePatch();
    if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
  };
  const goPrev = () => stepIdx > 0 && setStepIdx((i) => i - 1);

  const StepIcon = STEPS[stepIdx].icon;
  const stepId = STEPS[stepIdx].id;
  const filingRequiresSpouse = form.filing_status === "MFJ" || form.filing_status === "MFS";

  return (
    <>
      <div onClick={() => setOpen(true)} data-testid="demographics-open-trigger">
        {trigger}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="demographics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              <Sparkle size={18} weight="fill" className="text-emerald-600" />
              Client Onboarding Wizard
            </DialogTitle>
            <DialogDescription>
              These details flow straight into your tax return — the Drake preparer never has to re-key them.
            </DialogDescription>
            {session?.prefilled_from && (
              <div
                className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/70 px-2.5 py-1.5 text-[11px] text-emerald-900 flex items-center gap-1.5"
                data-testid="dw-prefill-badge"
              >
                <CheckCircle size={11} weight="fill" className="text-emerald-700" />
                Pre-filled from your {session.prefilled_from.tax_year || "prior-year"} return — just review &amp; confirm.
              </div>
            )}
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center gap-1 pb-3 border-b border-zinc-100" data-testid="demographics-stepper">
            {STEPS.map((s, i) => {
              const active = i === stepIdx;
              const done = i < stepIdx || (session?.completed && i === STEPS.length - 1);
              return (
                <div key={s.id} className="flex-1 flex flex-col items-center" data-testid={`demographics-step-${s.id}`}>
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors duration-200 ${
                      done ? "bg-emerald-600 text-white border-emerald-600" : active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-400 border-zinc-200"
                    }`}
                  >
                    {done ? <CheckCircle size={12} weight="fill" /> : <s.icon size={12} weight="fill" />}
                  </div>
                  <span className={`mt-1 text-[9.5px] font-medium ${active ? "text-slate-900" : "text-zinc-500"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>

          {isLoading ? (
            <div className="py-8 text-center"><CircleNotch size={20} className="animate-spin mx-auto text-zinc-400" /></div>
          ) : (
            <div className="py-3 min-h-[300px] space-y-3" data-testid={`demographics-panel-${stepId}`}>
              {stepId === "you" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First name" testid="dw-first-name">
                    <Input value={form.first_name || ""} onChange={(e) => setField("first_name", e.target.value)} data-testid="dw-first-name-input" />
                  </Field>
                  <Field label="Last name" testid="dw-last-name">
                    <Input value={form.last_name || ""} onChange={(e) => setField("last_name", e.target.value)} data-testid="dw-last-name-input" />
                  </Field>
                  <Field label="Middle initial">
                    <Input value={form.middle_initial || ""} onChange={(e) => setField("middle_initial", e.target.value)} maxLength={1} data-testid="dw-mi-input" />
                  </Field>
                  <Field label="Date of birth" hint="MM/DD/YYYY">
                    <Input value={form.dob || ""} onChange={(e) => setField("dob", e.target.value)} placeholder="05/15/1980" data-testid="dw-dob-input" />
                  </Field>
                  <Field label="Occupation">
                    <Input value={form.occupation || ""} onChange={(e) => setField("occupation", e.target.value)} data-testid="dw-occupation-input" />
                  </Field>
                  <Field label="Phone">
                    <Input value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)} data-testid="dw-phone-input" />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Email">
                      <Input type="email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="dw-email-input" />
                    </Field>
                  </div>
                </div>
              )}

              {stepId === "address" && (
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-6">
                    <Field label="Street address">
                      <Input value={form.address_line1 || ""} onChange={(e) => setField("address_line1", e.target.value)} data-testid="dw-addr1-input" />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field label="City">
                      <Input value={form.address_city || ""} onChange={(e) => setField("address_city", e.target.value)} data-testid="dw-city-input" />
                    </Field>
                  </div>
                  <div className="col-span-1">
                    <Field label="State" hint="2-letter">
                      <Input value={form.address_state || ""} onChange={(e) => setField("address_state", e.target.value.toUpperCase())} maxLength={2} data-testid="dw-state-input" />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="ZIP">
                      <Input value={form.address_zip || ""} onChange={(e) => setField("address_zip", e.target.value)} maxLength={10} data-testid="dw-zip-input" />
                    </Field>
                  </div>
                </div>
              )}

              {stepId === "family" && (
                <div className="space-y-4">
                  <Field label="Filing status">
                    <select
                      value={form.filing_status || ""}
                      onChange={(e) => setField("filing_status", e.target.value)}
                      className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white"
                      data-testid="dw-filing-status-select"
                    >
                      <option value="">Select…</option>
                      {FILING_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </Field>

                  {filingRequiresSpouse && (
                    <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50/50" data-testid="dw-spouse-block">
                      <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">Spouse</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="First name"><Input value={form.spouse?.first_name || ""} onChange={(e) => setSpouse("first_name", e.target.value)} data-testid="dw-spouse-first-input" /></Field>
                        <Field label="Last name"><Input value={form.spouse?.last_name || ""} onChange={(e) => setSpouse("last_name", e.target.value)} data-testid="dw-spouse-last-input" /></Field>
                        <Field label="Date of birth" hint="MM/DD/YYYY"><Input value={form.spouse?.dob || ""} onChange={(e) => setSpouse("dob", e.target.value)} data-testid="dw-spouse-dob-input" /></Field>
                        <Field label="SSN" hint="Stored encrypted"><Input value={form.spouse?.ssn || ""} onChange={(e) => setSpouse("ssn", e.target.value)} data-testid="dw-spouse-ssn-input" /></Field>
                      </div>
                    </div>
                  )}

                  <div className="rounded-md border border-zinc-200 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10.5px] uppercase tracking-wider text-zinc-500">Dependents ({(form.dependents || []).length})</p>
                      <button
                        onClick={addDependent}
                        className="text-[11px] font-medium text-emerald-700 hover:text-emerald-800 transition-colors duration-150"
                        data-testid="dw-add-dependent-btn"
                      >
                        + Add dependent
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(form.dependents || []).map((d, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`dw-dependent-${i}`}>
                          <div className="col-span-3">
                            <Field label="First name"><Input value={d.first_name || ""} onChange={(e) => setDep(i, "first_name", e.target.value)} data-testid={`dw-dep-${i}-first`} /></Field>
                          </div>
                          <div className="col-span-3">
                            <Field label="Last name"><Input value={d.last_name || ""} onChange={(e) => setDep(i, "last_name", e.target.value)} data-testid={`dw-dep-${i}-last`} /></Field>
                          </div>
                          <div className="col-span-3">
                            <Field label="SSN"><Input value={d.ssn || ""} onChange={(e) => setDep(i, "ssn", e.target.value)} data-testid={`dw-dep-${i}-ssn`} /></Field>
                          </div>
                          <div className="col-span-2">
                            <Field label="Rel.">
                              <select value={d.relationship || "child"} onChange={(e) => setDep(i, "relationship", e.target.value)} className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white" data-testid={`dw-dep-${i}-rel`}>
                                <option value="child">Child</option>
                                <option value="stepchild">Stepchild</option>
                                <option value="foster">Foster child</option>
                                <option value="parent">Parent</option>
                                <option value="other">Other</option>
                              </select>
                            </Field>
                          </div>
                          <div className="col-span-1">
                            <button onClick={() => removeDep(i)} className="text-[11px] text-rose-600 hover:text-rose-700 h-8" data-testid={`dw-dep-${i}-remove`}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {stepId === "bank" && (
                <div className="space-y-3">
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
                    These fields never auto-write — they route to preparer review for §6694 sign-off, per firm policy.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="SSN"><Input value={form.ssn || ""} onChange={(e) => setField("ssn", e.target.value)} data-testid="dw-ssn-input" placeholder="123-45-6789" /></Field>
                    <div />
                    <Field label="Bank routing #"><Input value={form.bank_routing || ""} onChange={(e) => setField("bank_routing", e.target.value)} maxLength={9} data-testid="dw-routing-input" /></Field>
                    <Field label="Bank account #"><Input value={form.bank_account || ""} onChange={(e) => setField("bank_account", e.target.value)} data-testid="dw-account-input" /></Field>
                    <Field label="Account type">
                      <select value={form.bank_type || "checking"} onChange={(e) => setField("bank_type", e.target.value)} className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white" data-testid="dw-account-type-select">
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {stepId === "review" && (
                <div className="space-y-3" data-testid="dw-review">
                  <div className="rounded-md border border-zinc-200 p-3 text-[12px] space-y-1">
                    <p><span className="text-zinc-500">Name:</span> {form.first_name} {form.middle_initial} {form.last_name}</p>
                    <p><span className="text-zinc-500">DOB:</span> {form.dob || "—"} · <span className="text-zinc-500">Occupation:</span> {form.occupation || "—"}</p>
                    <p><span className="text-zinc-500">Address:</span> {form.address_line1}, {form.address_city} {form.address_state} {form.address_zip}</p>
                    <p><span className="text-zinc-500">Filing status:</span> {form.filing_status || "—"} · <span className="text-zinc-500">Dependents:</span> {(form.dependents || []).length}</p>
                    <p><span className="text-zinc-500">Bank:</span> ****{(form.bank_account || "").slice(-4) || "—"} ({form.bank_type})</p>
                  </div>
                  <p className="text-[11px] text-zinc-500">
                    Clicking <span className="font-medium">Submit</span> writes these into your engagement so the preparer never has to re-key.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={stepIdx === 0} data-testid="dw-prev-btn">
              <ArrowLeft size={13} className="mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <button
                onClick={savePatch}
                className="text-[11px] text-zinc-500 hover:text-slate-900 transition-colors duration-150"
                data-testid="dw-save-btn"
              >
                {patch.isPending ? "Saving…" : "Save & close"}
              </button>
              {stepIdx < STEPS.length - 1 ? (
                <Button size="sm" onClick={goNext} data-testid="dw-next-btn">
                  Next <ArrowRight size={13} className="ml-1" />
                </Button>
              ) : (
                <Button size="sm" onClick={() => submit.mutate()} disabled={submit.isPending} data-testid="dw-submit-btn">
                  {submit.isPending ? <><CircleNotch size={13} className="animate-spin mr-1" /> Submitting…</> : <>Submit & finish</>}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
